import * as t from "io-ts";
import { SWRConfiguration } from "swr/dist/types";
import useSWR from "swr";
import { flow, identity, pipe } from "fp-ts/function";
import { either, option } from "fp-ts";
import { errorsToMessage } from "./fp";
import * as Sentry from "@sentry/browser";

interface ApiError {
    kind: "network" | "unexpectedFormat" | "unknown";
    message: string;
}

interface LoadingState {
    type: "loading";
}

interface ErrorState extends ApiError {
    type: "error";
}

interface CompletedState<Data> {
    type: "completed";
    data: Data;
}

class ApiErrorThrowable extends Error {
    constructor(message: string, public kind: ApiError["kind"]) {
        super(message);
    }
}

type State<Row> = ErrorState | CompletedState<Row> | LoadingState;

type ApiCallId = string;

export const useApi = <DataCodec extends t.Mixed>(
    id: ApiCallId,
    query: (id: ApiCallId) => Promise<unknown>,
    dataCodec: DataCodec,
    swrConfig?: SWRConfiguration<t.TypeOf<DataCodec>, Error>
) => {
    type Data = t.TypeOf<DataCodec>;
    const { data, error, ...rest } = useSWR(
        id,
        async () => {
            let res;
            try {
                res = await query(id);
            } catch (e) {
                throw new ApiErrorThrowable(String(e), "network");
            }

            return pipe(
                res,
                dataCodec.decode,
                either.mapLeft(
                    flow(
                        errorsToMessage,
                        (message): ApiError => ({
                            kind: "unexpectedFormat",
                            message,
                        })
                    )
                ),
                either.fold((e) => {
                    Sentry.captureMessage(`${id}: ${e.kind} - ${e.message}`);
                    throw new ApiErrorThrowable(e.message, e.kind);
                }, identity)
            );
        },
        swrConfig
    );

    const state: State<Data> = pipe(
        data,
        option.fromNullable,
        option.fold(
            () =>
                pipe(
                    error,
                    option.fromNullable,
                    option.fold(
                        () => ({
                            type: "loading",
                        }),
                        (error): State<Data> => ({
                            type: "error",
                            kind: "network",
                            message: String(error),
                        })
                    )
                ),
            (data): State<Data> => ({
                type: "completed",
                data,
            })
        )
    );

    return {
        ...rest,
        state,
    };
};
